import { IPracticeRepository } from '../../domain/repositories/IPracticeRepository';
import { Practice } from '../../domain/entities/Practice';
import { Distance } from '../../domain/valueObjects/Distance';
import { Duration } from '../../domain/valueObjects/Duration';
import { ConfigManager } from '../../application/config/Config';
import { LoggerFactory } from '../../application/logging/Logger';
import { InternalServerError } from '../../application/errors/AppError';
import { PracticeApiResponse } from '../../types/index';
import { NormalResponse } from '../../types/index';

/**
 * API-based Practice Repository Implementation
 * Follows Repository Pattern and Dependency Inversion Principle
 */
export class PracticeRepository implements IPracticeRepository {
  private readonly config = ConfigManager.getInstance().getApiConfig();
  private readonly logger = LoggerFactory.getLogger();

  async findById(id: string): Promise<Practice | null> {
    try {
      const response = await fetch(`${this.config.baseUrl}/practices/${id}`, {
        method: 'GET',
        headers: {
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (!response.ok) {
        if (response.status === 404) {
          return null;
        }
        throw new InternalServerError('Failed to fetch practice');
      }

      const data = await response.json();
      return Practice.fromData(data);
    } catch (error) {
      this.logger.error('Failed to find practice by ID', error as Error, { practiceId: id });
      throw error;
    }
  }

  async findByUserId(userId: string): Promise<PracticeApiResponse> {
  try {
    const token = localStorage.getItem('auth_token');
    
    const response = await fetch(`${this.config.baseUrl}/practice/practice_data/${userId}`, {
      method: 'GET',
      headers: {
        'X-API-Key': this.config.apiKey,
        'Authorization': `Bearer ${token}`,
        "Content-Type": "application/x-www-form-urlencoded",
      }
    });

    if (!response.ok) {
      throw new InternalServerError('Failed to fetch user practices');
    }

    const data = await response.json();
    return data.data; // { practice: [...], laps: [...] }
  } catch (error) {
    this.logger.error('Failed to find practices by user ID', error as Error, { userId });
    throw error;
  }
}
      
  async findByDateRange(userId: string, startDate: Date, endDate: Date): Promise<Practice[]> {
    try {
      const params = new URLSearchParams({
        user_id: userId,
        start_date: startDate.toISOString(),
        end_date: endDate.toISOString()
      });

      const response = await fetch(`${this.config.baseUrl}/practices?${params}`, {
        method: 'GET',
        headers: {
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (!response.ok) {
        throw new InternalServerError('Failed to fetch practices by date range');
      }

      const data = await response.json();
      return data.practices?.map((practice: unknown) => Practice.fromData(practice as any)) || [];
    } catch (error) {
      this.logger.error('Failed to find practices by date range', error as Error, { 
        userId, 
        startDate, 
        endDate 
      });
      throw error;
    }
  }

  async findByFolder(folderId: string): Promise<Practice[]> {
    try {
      const response = await fetch(`${this.config.baseUrl}/folder-practices/${folderId}/practices`, {
        method: 'GET',
        headers: {
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (!response.ok) {
        throw new InternalServerError('Failed to fetch folder practices');
      }

      const data = await response.json();
      return data.practices?.map((practice: unknown) => Practice.fromData(practice as any)) || [];
    } catch (error) {
      this.logger.error('Failed to find practices by folder', error as Error, { folderId });
      throw error;
    }
  }

  async save(
  coachId: number,
  swimmerId: number,
  practiceModel: Practice,
  lapModels: any[] = [],
  update: string = ""
): Promise<Practice> {
  try {
    const parameters = {
      coach_id: coachId,
      swimmer_id: swimmerId,
      name: practiceModel.name,
      description: practiceModel.description,
      practice_id: practiceModel.id || 0,
      rep: lapModels.length,
      laps_id: update,
      practice_date: new Date(practiceModel.date)
        .toLocaleString("sv-SE", { timeZone: "UTC" })
        .replace("T", " "),
      practice_time: practiceModel.practice_time,
      practice_CourseTypeLookupId: practiceModel.courseTypeLookupId || 0,
      createdAt: new Date(practiceModel.createdAt)
        .toLocaleString("sv-SE", { timeZone: "UTC" })
        .replace("T", " "),
      updated_at: new Date().toLocaleString("sv-SE", { timeZone: "UTC" }).replace("T", " "),
      isFavorite: practiceModel.isFavorite ? 1 : 0,
      imageData: practiceModel.imageData || null,
      isFinished: practiceModel.isFinished ? "1" : "0",
      laps: lapModels,
    };
    this.logger.debug("Practice save request", {
      coachId,
      swimmerId,
      practiceId: parameters.practice_id,
      practiceIdLength: String(parameters.practice_id ?? "").length,
      name: parameters.name,
      practice_time: parameters.practice_time,
      courseTypeLookupId: parameters.practice_CourseTypeLookupId,
      lapsCount: lapModels.length,
      firstLap:
        lapModels && lapModels.length > 0
          ? {
              rep: lapModels[0]?.rep,
              distance: lapModels[0]?.distance,
              interval: lapModels[0]?.interval ?? lapModels[0]?.interval_lap,
              practice_id: lapModels[0]?.practice_id,
              practice_lap_id: lapModels[0]?.practice_lap_id,
            }
          : null,
    });

    const response = await fetch(
      `${this.config.baseUrl}/practice/manage_coach_practice`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-API-Key": this.config.apiKey,
          Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
        },
        body: JSON.stringify(parameters),
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to save practice (${response.status})`);
    }

    const data = await response.json();
    this.logger.debug("Practice save response", {
      status: data.status,
      msg: data.msg,
      success: data.success,
      practiceId: data.data?.practice_id,
      hasData: Boolean(data.data),
    });

    if (data.status === 0) {
      throw new Error(data.msg || "Error from backend");
    }

    //  Safe fallback if backend returns null
    if (!data.data) {
      this.logger.warn("Backend returned null data for practice save", {
        practiceId: practiceModel.id,
      });

      // Use the same model so app doesn’t crash
      return practiceModel;
    }

    return Practice.fromData(data.data);
  } catch (error) {
    this.logger.error("Failed to save practice", error as Error, {
      practiceId: practiceModel.id,
      practiceIdLength: String(practiceModel.id ?? "").length,
      lapsCount: lapModels.length,
    });
    throw error;
  }
}

async storeFolderPractice(
  coachId: number,
  swimmerId: number,
  practiceModel: Practice,
  lapModels: any[] = [],
  _update: string = ""
): Promise<Practice> {
  try {
    const nowUtc = new Date().toISOString().slice(0, 19).replace("T", " ");
    const targetModelId = String(practiceModel.id || 0);

    // Folder endpoint expects folder-practice lap DTO keys.
    const folderPracticeLaps = (lapModels || []).map((lap: any) => ({
      createdAt:
        lap.createdAt ||
        lap.created_at ||
        nowUtc,
      description: lap.description || "",
      distance: String(lap.distance ?? 0),
      energy_zone: lap.energy_zone || "Warm Up",
      folder_practice_lap_id: String(
        lap.folder_practice_lap_id ?? lap.practice_lap_id ?? lap.id ?? 0
      ),
      folder_practice_model_id: String(
        lap.folder_practice_model_id ?? targetModelId
      ),
      interval: String(lap.interval ?? lap.interval_lap ?? 0),
      isnoEditable:
        Number(lap.isnoEditable ?? (lap.isNoEditable ? 1 : 0)) > 0 ? 1 : 0,
      passedRapNumber: Number(lap.passedRapNumber ?? lap.passedRepNumber ?? 0),
      rep: String(lap.rep ?? 1),
    }));

    const parameters = {
      folder_id: practiceModel.folderId,
      name: practiceModel.name,
      description: practiceModel.description,
      practice_date: new Date(practiceModel.date)
        .toLocaleString("sv-SE", { timeZone: "UTC" })
        .replace("T", " "),
      practice_time: practiceModel.practice_time,
      CourseTypeLookupId: practiceModel.courseTypeLookupId || 0,
      coach_id: coachId,
      swimmer_id: swimmerId,
      isFavorite: practiceModel.isFavorite ? 1 : 0,
      imageData: practiceModel.imageData || null,
      isFinished: practiceModel.isFinished ? "1" : "0",
      laps: folderPracticeLaps,
    };

    const response = await fetch(
      `${this.config.baseUrl}/folderpractice/manage_folder_practice_model`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-API-Key": this.config.apiKey,
          Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
        },
        body: JSON.stringify(parameters),
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to save practice (${response.status})`);
    }

    const data = await response.json();
    this.logger.debug("Folder practice save response", {
      status: data.status,
      practiceId: data.data?.practice_id,
      folderPracticeModelId: data.data?.folder_practice_model_id,
      sentFolderId: practiceModel.folderId,
      sentLapCount: folderPracticeLaps.length,
      sentModelId: targetModelId,
    });

    if (data.status === 0) {
      throw new Error(data.msg || "Error from backend");
    }

    //  Safe fallback if backend returns null
    if (!data.data) {
      this.logger.warn("Backend returned null data for practice save", {
        practiceId: practiceModel.id,
      });

      // Use the same model so app doesn’t crash
      return practiceModel;
    }

    return Practice.fromData(data.data);
  } catch (error) {
    this.logger.error("Failed to save practice", error as Error, {
      practiceId: practiceModel.id,
    });
    throw error;
  }
}

  async removePracticeLap(  
    practiceId: number,
    practiceLapId: number,
    swimmerId: number
  ): Promise<NormalResponse> {
    const parameters = {
      practice_id: practiceId,
      practice_lap_id: practiceLapId,
      swimmer_id: swimmerId,
    };

    try {
      const response = await fetch(`${this.config.baseUrl}/practice/remove_practice_lap`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-API-Key": this.config.apiKey,
          Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
        },
        body: JSON.stringify(parameters),
      });

      const result: NormalResponse = await response.json();
      return result;
    } catch (error) {
      this.logger.error("Error removing practice lap", error as Error, { practiceId, practiceLapId });
      throw error;
    }
  }

  async update(practice: Practice): Promise<Practice> {
    try {
      const response = await fetch(`${this.config.baseUrl}/practices/${practice.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify(practice.toJSON())
      });

      if (!response.ok) {
        throw new InternalServerError('Failed to update practice');
      }

      const data = await response.json();
      return Practice.fromData(data);
    } catch (error) {
      this.logger.error('Failed to update practice', error as Error, { practiceId: practice.id });
      throw error;
    }
  }

  async delete(id: string, swimmerId: number): Promise<void> {
  try {
    const formData = new FormData();
    formData.append("practice_id", id);
    formData.append("swimmer_id", swimmerId.toString());

    const response = await fetch(`${this.config.baseUrl}/practice/remove_practice`, {
      method: "POST",
      headers: {
        "X-API-Key": this.config.apiKey,
        "Authorization": `Bearer ${localStorage.getItem("auth_token")}`,
      },
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`Failed to delete practice (status: ${response.status})`);
    }

    const text = await response.text();
    if (text) {
      try {
        const data = JSON.parse(text);
        this.logger.debug("Delete practice response", { practiceId: id, response: data });
      } catch {
        this.logger.debug("Delete practice response (non-JSON)", { practiceId: id, response: text });
      }
    }
  } catch (error) {
    this.logger.error("Failed to delete practice", error as Error, { practiceId: id, swimmerId });
    throw error;
  }
}

  async getTotalDistance(userId: string): Promise<Distance> {
    try {
      const response = await fetch(`${this.config.baseUrl}/practices/stats?user_id=${userId}`, {
        method: 'GET',
        headers: {
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (!response.ok) {
        throw new InternalServerError('Failed to fetch total distance');
      }

      const data = await response.json();
      return Distance.fromMeters(data.total_distance || 0);
    } catch (error) {
      this.logger.error('Failed to get total distance', error as Error, { userId });
      throw error;
    }
  }

  async getTotalDuration(userId: string): Promise<Duration> {
    try {
      const response = await fetch(`${this.config.baseUrl}/practices/stats?user_id=${userId}`, {
        method: 'GET',
        headers: {
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (!response.ok) {
        throw new InternalServerError('Failed to fetch total duration');
      }

      const data = await response.json();
      return Duration.fromMinutes(data.total_duration || 0);
    } catch (error) {
      this.logger.error('Failed to get total duration', error as Error, { userId });
      throw error;
    }
  }

  async getTotalCalories(userId: string): Promise<number> {
    try {
      const response = await fetch(`${this.config.baseUrl}/practices/stats?user_id=${userId}`, {
        method: 'GET',
        headers: {
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (!response.ok) {
        throw new InternalServerError('Failed to fetch total calories');
      }

      const data = await response.json();
      return data.total_calories || 0;
    } catch (error) {
      this.logger.error('Failed to get total calories', error as Error, { userId });
      throw error;
    }
  }

  async getAveragePace(userId: string): Promise<number> {
    try {
      const response = await fetch(`${this.config.baseUrl}/practices/stats?user_id=${userId}`, {
        method: 'GET',
        headers: {
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (!response.ok) {
        throw new InternalServerError('Failed to fetch average pace');
      }

      const data = await response.json();
      return data.average_pace || 0;
    } catch (error) {
      this.logger.error('Failed to get average pace', error as Error, { userId });
      throw error;
    }
  }

  async getPracticeCount(userId: string): Promise<number> {
    try {
      const response = await fetch(`${this.config.baseUrl}/practices/stats?user_id=${userId}`, {
        method: 'GET',
        headers: {
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (!response.ok) {
        throw new InternalServerError('Failed to fetch practice count');
      }

      const data = await response.json();
      return data.total_practices || 0;
    } catch (error) {
      this.logger.error('Failed to get practice count', error as Error, { userId });
      throw error;
    }
  }

  async search(userId: string, query: string): Promise<Practice[]> {
    try {
      const params = new URLSearchParams({
        user_id: userId,
        search: query
      });

      const response = await fetch(`${this.config.baseUrl}/practices/search?${params}`, {
        method: 'GET',
        headers: {
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (!response.ok) {
        throw new InternalServerError('Failed to search practices');
      }

      const data = await response.json();
      return data.practices?.map((practice: unknown) => Practice.fromData(practice as any)) || [];
    } catch (error) {
      this.logger.error('Failed to search practices', error as Error, { userId, query });
      throw error;
    }
  }

  async findByFilters(filters: {
    userId: string;
    startDate?: Date;
    endDate?: Date;
    minDistance?: number;
    maxDistance?: number;
    stroke?: string;
  }): Promise<Practice[]> {
    try {
      const params = new URLSearchParams({ user_id: filters.userId });
      
      if (filters.startDate) params.append('start_date', filters.startDate.toISOString());
      if (filters.endDate) params.append('end_date', filters.endDate.toISOString());
      if (filters.minDistance) params.append('min_distance', filters.minDistance.toString());
      if (filters.maxDistance) params.append('max_distance', filters.maxDistance.toString());
      if (filters.stroke) params.append('stroke', filters.stroke);

      const response = await fetch(`${this.config.baseUrl}/practices/filter?${params}`, {
        method: 'GET',
        headers: {
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (!response.ok) {
        throw new InternalServerError('Failed to filter practices');
      }

      const data = await response.json();
      return data.practices?.map((practice: unknown) => Practice.fromData(practice as any)) || [];
    } catch (error) {
      this.logger.error('Failed to filter practices', error as Error, { filters });
      throw error;
    }
  }

  async findPaginated(userId: string, page: number, limit: number): Promise<{
    practices: Practice[];
    rawPractices: any[];
    laps: any[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  }> {
    try {
      const token = localStorage.getItem('auth_token');
      
      const response = await fetch(`${this.config.baseUrl}/practice/practice_data_paginated/${userId}?page=${page}&limit=${limit}`, {
        method: 'GET',
        headers: {
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${token}`,
          "Content-Type": "application/x-www-form-urlencoded",
        }
      });

      if (!response.ok) {
        throw new InternalServerError('Failed to fetch practices for pagination');
      }

      const data = await response.json();
      const apiData = data.data || {};
      const rawPractices = apiData.practice || apiData.practices || [];
      const rawLaps = apiData.laps || [];
      
      const allPractices =
        rawPractices.map((practice: any) => {
          // Map API response to Practice entity format
          const mappedPractice = {
              id: String(practice.id || practice.practice_id || ""),
              practice_id: String(practice.practice_id || practice.id || ""),
              coach_id: Number(practice.coach_id || practice.CoachId || 0),
              swimmer_id: Number(practice.swimmer_id || practice.SwimmerId || 0),
              name: practice.name || practice.PracticeName || "",
              description: practice.description || practice.Description || "",
              practice_date: practice.practice_date || practice.PracticeDate || practice.date || "",
              practice_time: practice.practice_time?.toString() || practice.duration?.toString() || "0",
              practice_CourseTypeLookupId: Number(practice.practice_CourseTypeLookupId || practice.CourseTypeId || 0),
              distance: Number(practice.distance) || 0,
              calories: Number(practice.calories) || 0,
              createdAt: practice.createdAt || practice.created_at || "",
              updatedAt: practice.updatedAt || practice.updated_at || "",
            };

          return Practice.fromData(mappedPractice);
        }) || [];
      
      return {
        practices: allPractices,
        rawPractices,
        laps: rawLaps,
        total: allPractices.length,
        page: page,
        limit: limit,
        totalPages: Math.ceil(allPractices.length / limit)
      };
    } catch (error) {
      this.logger.error('Failed to get paginated practices', error as Error, { 
        userId, 
        page, 
        limit 
      });
      throw error;
    }
  }



  async getAllPractices(userId: string): Promise<{
  practices: Practice[];
  laps: any[];
}> {
  try {
    const token = localStorage.getItem('auth_token');
      
    const response = await fetch(
      `${this.config.baseUrl}/practice/practice_data/${userId}`,
      {
        method: 'GET',
        headers: {
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${token}`,
          "Content-Type": "application/x-www-form-urlencoded",
        }
      }
    );

    if (!response.ok) {
      throw new InternalServerError('Failed to fetch practices for pagination');
    }

    const data = await response.json();
    const apiData = data.data || {};

    const rawPractices = apiData.practice || [];
    const rawLaps = apiData.laps || [];

    const allPractices =
      rawPractices.map((practice: any) => {
        const mappedPractice = {
          id: String(practice.id || practice.practice_id || ""),
              practice_id: String(practice.practice_id || practice.id || ""),
              coach_id: Number(practice.coach_id || practice.CoachId || 0),
              swimmer_id: Number(practice.swimmer_id || practice.SwimmerId || 0),
              name: practice.name || practice.PracticeName || "",
              description: practice.description || practice.Description || "",
              practice_date: practice.practice_date || practice.PracticeDate || practice.date || "",
              practice_time: practice.practice_time?.toString() || practice.duration?.toString() || "0",
              practice_CourseTypeLookupId: Number(
                practice.practice_CourseTypeLookupId ||
                practice.CourseTypeId ||
                0
              ),
              distance: Number(practice.distance) || 0,
              calories: Number(practice.calories) || 0,
              createdAt: practice.createdAt || practice.created_at || "",
              updatedAt: practice.updatedAt || practice.updated_at || "",
              isFavorite: practice.isFavorite === "1" || practice.isFavorite === 1,
        };

        return Practice.fromData(mappedPractice);
      }) || [];

      console.log('laps' , rawLaps);
    return {
      practices: allPractices,
      laps: rawLaps
    };

  } catch (error) {
    this.logger.error(
      'Failed to get paginated practices',
      error as Error,
      { userId }
    );
    throw error;
  }
}


}
